'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { TrendingUp, DollarSign, Target, Award, Calculator } from 'lucide-react';

export function PerformanceManagement() {
  const [selectedPeriod, setSelectedPeriod] = useState('2024-Q1');

  const performanceData = [
    {
      id: 1,
      employee: '김개발',
      position: '프론트엔드 개발자',
      department: '개발부',
      jobGrade: 'L3',
      baseScore: 85,
      competencyScore: 88,
      achievementScore: 92,
      totalScore: 88,
      currentSalary: 6200,
      adjustedSalary: 6500,
      bonus: 800,
      status: '확정'
    },
    {
      id: 2,
      employee: '박영업',
      position: '영업 매니저',
      department: '영업부',
      jobGrade: 'L4',
      baseScore: 92,
      competencyScore: 90,
      achievementScore: 95,
      totalScore: 92,
      currentSalary: 8100,
      adjustedSalary: 8500,
      bonus: 1200,
      status: '확정'
    },
    {
      id: 3,
      employee: '이마케팅',
      position: '마케팅 스페셜리스트',
      department: '마케팅부',
      jobGrade: 'L3',
      baseScore: 78,
      competencyScore: 82,
      achievementScore: 75,
      totalScore: 78,
      currentSalary: 6200,
      adjustedSalary: 6300,
      bonus: 400,
      status: '검토중'
    }
  ];

  const teamPerformanceData = [
    { team: '개발부', avgScore: 86, targetScore: 85, achievement: 101 },
    { team: '영업부', avgScore: 89, targetScore: 88, achievement: 101 },
    { team: '마케팅부', avgScore: 82, targetScore: 85, achievement: 96 },
    { team: '인사부', avgScore: 87, targetScore: 85, achievement: 102 }
  ];

  const salaryEquityData = [
    { grade: 'L1', minSalary: 3000, avgSalary: 3500, maxSalary: 4000, marketRate: 3600 },
    { grade: 'L2', minSalary: 4000, avgSalary: 4800, maxSalary: 5500, marketRate: 4900 },
    { grade: 'L3', minSalary: 5500, avgSalary: 6200, maxSalary: 7000, marketRate: 6300 },
    { grade: 'L4', minSalary: 7000, avgSalary: 8100, maxSalary: 9000, marketRate: 8200 },
    { grade: 'L5', minSalary: 9000, avgSalary: 10500, maxSalary: 12000, marketRate: 10800 }
  ];

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold">성과·보상 관리</h1>
          <p className="text-xs sm:text-sm text-gray-600">직무등급과 개인 성과를 연계한 보상 관리</p>
        </div>
        <div className="flex space-x-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-28 sm:w-40 text-xs sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024-Q1">2024년 1분기</SelectItem>
              <SelectItem value="2023-Q4">2023년 4분기</SelectItem>
              <SelectItem value="2023-Q3">2023년 3분기</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" className="text-xs sm:text-sm">
            <Calculator className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">보상 </span>시뮬레이션
          </Button>
        </div>
      </div>

      {/* Performance Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
            <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">평균 성과점수</CardTitle>
            <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-lg sm:text-2xl font-bold">86.5점</div>
            <p className="text-[10px] sm:text-xs text-gray-500">
              전년 대비 +3.2점
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
            <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">보상 총액</CardTitle>
            <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-lg sm:text-2xl font-bold">2.4억원</div>
            <p className="text-[10px] sm:text-xs text-gray-500">
              전년 대비 +8.5%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
            <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">목표 달성률</CardTitle>
            <Target className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-lg sm:text-2xl font-bold">94.8%</div>
            <p className="text-[10px] sm:text-xs text-gray-500">
              조직 전체 평균
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
            <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">우수 성과자</CardTitle>
            <Award className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-lg sm:text-2xl font-bold">23명</div>
            <p className="text-[10px] sm:text-xs text-gray-500">
              전체의 18.5%
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="individual" className="w-full">
        <TabsList>
          <TabsTrigger value="individual" className="text-[10px] sm:text-sm px-2 sm:px-4">개인 성과</TabsTrigger>
          <TabsTrigger value="team" className="text-[10px] sm:text-sm px-2 sm:px-4">팀별 현황</TabsTrigger>
          <TabsTrigger value="equity" className="text-[10px] sm:text-sm px-2 sm:px-4">보상 형평성</TabsTrigger>
        </TabsList>

        <TabsContent value="individual" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">개인별 성과 및 보상</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무등급별 성과점수와 보상 반영 결과</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">직원</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">등급</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden sm:table-cell">성과</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden md:table-cell">급여변동</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">성과급</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap text-right">상태</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {performanceData.map((person) => (
                    <TableRow key={person.id}>
                      <TableCell>
                        <div className="font-medium text-xs sm:text-sm">{person.employee}</div>
                        <div className="text-[10px] sm:text-sm text-muted-foreground truncate max-w-[80px] sm:max-w-none">{person.position}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] sm:text-xs">{person.jobGrade}</Badge>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <span className="text-xs sm:text-sm">{person.totalScore}점</span>
                          <Progress value={person.totalScore} className="w-10 sm:w-12" />
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="text-[10px] sm:text-sm text-muted-foreground">{person.currentSalary.toLocaleString()}</div>
                        <div className="text-green-600 font-medium text-xs sm:text-sm">→ {person.adjustedSalary.toLocaleString()}</div>
                      </TableCell>
                      <TableCell className="text-blue-600 font-medium text-xs sm:text-sm whitespace-nowrap">
                        {person.bonus.toLocaleString()}만원
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={person.status === '확정' ? 'default' : 'secondary'} className="text-[10px] sm:text-xs">
                          {person.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">팀별 성과 현황</CardTitle>
              <CardDescription className="text-xs sm:text-sm">부서별 목표 달성도 및 평균 성과</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={teamPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="team" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="avgScore" fill="#3b82f6" name="평균 성과점수" />
                  <Bar dataKey="targetScore" fill="#e2e8f0" name="목표 점수" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">팀별 성과급 및 인센티브</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">팀명</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm hidden sm:table-cell">평균 점수</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">달성률</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm hidden sm:table-cell">팀 성과급</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">1인당</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teamPerformanceData.map((team) => (
                      <TableRow key={team.team}>
                        <TableCell className="font-medium whitespace-nowrap text-xs sm:text-sm">{team.team}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs sm:text-sm hidden sm:table-cell">{team.avgScore}점</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <span className="whitespace-nowrap text-xs sm:text-sm">{team.achievement}%</span>
                            <Progress value={team.achievement} className="w-10 sm:w-16" />
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs sm:text-sm hidden sm:table-cell">
                          {(team.achievement * 50).toLocaleString()}만원
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs sm:text-sm">
                          {Math.round(team.achievement * 50 / 10).toLocaleString()}만원
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="equity" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">급여 형평성 분석</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무등급별 내부 및 시장 급여 비교</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={salaryEquityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="grade" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(value) => [`${value}만원`, '']} />
                  <Line type="monotone" dataKey="avgSalary" stroke="#3b82f6" strokeWidth={2} name="내부 평균급여" />
                  <Line type="monotone" dataKey="marketRate" stroke="#ef4444" strokeWidth={2} name="시장 수준" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">급여 형평성 지수</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <div className="text-center p-2 sm:p-4 border rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-green-600">98.5%</div>
                  <div className="text-[10px] sm:text-sm text-gray-600">내부 형평성</div>
                </div>
                <div className="text-center p-2 sm:p-4 border rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-blue-600">102.3%</div>
                  <div className="text-[10px] sm:text-sm text-gray-600">시장 경쟁력</div>
                </div>
                <div className="text-center p-2 sm:p-4 border rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-purple-600">96.8%</div>
                  <div className="text-[10px] sm:text-sm text-gray-600">성별 형평성</div>
                </div>
                <div className="text-center p-2 sm:p-4 border rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-orange-600">94.2%</div>
                  <div className="text-[10px] sm:text-sm text-gray-600">경력 형평성</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
